/**
 * ConsentAffirmation Component
 * 
 * Renders the consent affirmation display for both view and edit modes.
 * Uses snapshot text for signed documents (compliance), otherwise fetches from backend.
 */

import React, { useState, useEffect } from 'react';
import { Users, Monitor, Phone, Building, Lock } from 'lucide-react';
import { Badge } from '../ui/badge';
import { fetchConsentNarrative, getModalityDisplayInfo } from '../../utils/consentHelpers';

/**
 * ConsentAffirmationDisplay - View mode component
 * 
 * @param {Object} props
 * @param {Object} props.consentData - The consent data object with pos_code, modality, provider_location
 * @param {Object} props.snapshot - Optional snapshot from signed document (takes precedence)
 * @param {string} props.token - Auth token for API calls
 * @param {boolean} props.compact - If true, shows compact version
 * @param {boolean} props.isSigned - If true, shows "locked" indicator
 */
export function ConsentAffirmationDisplay({ consentData, snapshot, token, compact = false, isSigned = false }) {
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usingSnapshot, setUsingSnapshot] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    async function loadNarrative() {
      // If we have a valid snapshot, use it (compliance - preserve signed text)
      if (snapshot?.text && snapshot?.version) {
        setNarrative({
          text: snapshot.text,
          modality: snapshot.modality || consentData?.modality,
          version: snapshot.version,
          is_valid: true,
          captured_at: snapshot.captured_at
        });
        setUsingSnapshot(true);
        setLoading(false);
        return;
      }
      
      if (!consentData || !token) {
        setLoading(false);
        return;
      }
      
      try {
        const result = await fetchConsentNarrative(consentData, token);
        if (mounted) {
          setNarrative(result);
          setUsingSnapshot(false);
        }
      } catch (error) {
        console.error('Error loading consent narrative:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }
    
    loadNarrative();
    
    return () => {
      mounted = false;
    };
  }, [consentData, snapshot, token]);

  // Show nothing if no consent data or not valid
  if (!consentData?.pos_code && !consentData?.modality && !snapshot?.text) {
    return (
      <div className="w-full">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-500">Consent Affirmation</span>
          </div>
          <p className="text-sm text-gray-400 text-center py-2">
            No consent affirmation recorded for this note.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-gray-300 rounded"></div>
            <div className="h-4 w-32 bg-gray-300 rounded"></div>
          </div>
          <div className="h-4 w-full bg-gray-200 rounded mt-2"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded mt-1"></div>
        </div>
      </div>
    );
  }

  if (!narrative?.is_valid) {
    return (
      <div className="w-full">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-500">Consent Affirmation</span>
          </div>
          <p className="text-sm text-gray-400 text-center py-2">
            Unable to load consent affirmation.
          </p>
        </div>
      </div>
    );
  }

  const displayInfo = getModalityDisplayInfo(narrative.modality);
  const IconComponent = narrative.modality === 'in_person' ? Users 
    : narrative.modality === 'audio_visual' ? Monitor 
    : Phone;

  return (
    <div className="w-full" data-testid="consent-affirmation-display">
      <div className={`border-2 rounded-lg p-4 ${displayInfo.bgColor} ${displayInfo.borderColor}`}>
        <div className="flex items-start gap-2">
          <IconComponent className={`h-5 w-5 ${displayInfo.iconColor} mt-0.5`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-gray-900">Consent Affirmation</span>
              <Badge className="bg-green-200 text-green-800 text-xs">✓ Confirmed</Badge>
              {(isSigned || usingSnapshot) && (
                <Badge className="bg-gray-200 text-gray-600 text-xs flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Signed
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-700">{narrative.text}</p>
            {!compact && (
              <p className="text-xs text-gray-400 mt-2">
                {usingSnapshot ? (
                  <>Consent language v{narrative.version} (captured at signing)</>
                ) : (
                  <>Consent language v{narrative.version}</>
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsentAffirmationDisplay;
